import { AxiosError } from 'axios';
import { FormikHelpers } from 'formik';
import { omit } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { generatePath, useNavigate } from 'react-router-dom';

import { shopsApi } from 'api';
import { NEW_ID } from 'constants/common.constants';
import { ROUTE_PATH } from 'constants/routes';
import { QueryKey } from 'enums';
import { useMutation, useUser } from 'hooks';
import { Shop } from 'types';
import { validationUtils } from 'utils';

import { shopWebhookOverridesUtils } from '../../shop-webhook-overrides.utils';

type UseShopSubmitOptions = {
  shopId: string;
  onSuccess?: (data: Shop) => void;
};

export const useShopSubmit = ({ shopId, onSuccess }: UseShopSubmitOptions) => {
  const { apiRole, isAdmin, isTechOperator, isMerchant, isMerchantOperator } =
    useUser();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isNew = shopId === NEW_ID;

  const shopDetailsPath = useMemo(() => {
    if (isAdmin) {
      return ROUTE_PATH.ADMIN.SHOP_DETAILS;
    } else if (isTechOperator) {
      return ROUTE_PATH.TECH_OPERATOR.SHOP_DETAILS;
    } else if (isMerchant) {
      return ROUTE_PATH.MERCHANT.SHOP_DETAILS;
    }
  }, [isAdmin, isTechOperator, isMerchant]);

  const { mutate: createShop } = useMutation<Shop, AxiosError, Partial<Shop>>(
    shopsApi.createAsRole(apiRole),
    { mergeNotifierMessages: { success: null } },
  );
  const { mutate: updateShop } = useMutation<
    Shop,
    AxiosError,
    { id: string; data: Partial<Shop> }
  >(shopsApi.updateAsRole(apiRole), {
    mergeNotifierMessages: { success: null },
  });

  const handleSubmit = useCallback(
    <T extends Record<string, any>>(
      values: T,
      formikHelpers: FormikHelpers<T>,
      omitFields?: string[],
    ) => {
      const options = {
        onSuccess: (data: Shop) => {
          formikHelpers.resetForm({ values });
          queryClient.invalidateQueries(QueryKey.Shops);
          if (isNew) {
            navigate(generatePath(shopDetailsPath!, { id: data.id }), {
              replace: true,
            });
          }
          onSuccess?.(data);
        },
        onError: (error: AxiosError) =>
          formikHelpers.setErrors(validationUtils.getFormErrors(error) as any),
        onSettled: () => formikHelpers.setSubmitting(false),
      };

      formikHelpers.setSubmitting(true);

      let shop: Partial<Shop> = { ...values };
      if ('webhookOverrides' in shop) {
        shop.webhookOverrides = shopWebhookOverridesUtils.normalize(
          shop.webhookOverrides,
        );
      }

      if (!isAdmin) {
        shop = omit(shop, 'withdrawalFeeAmount');
      }

      if (isMerchant || isMerchantOperator) {
        shop = omit(
          shop,
          'payinOrderMinAmount',
          'payinOrderMaxAmount',
          'maxParallelCustomerOrders',
          'payoutOrderMinAmount',
          'payoutOrderMaxAmount',
          'payinTimeoutConfig',
          'payoutMaxTimeout',
          'payinFiatCurrencyExchangeConfigs',
          'payinAssetCurrencyExchangeId',
          'payinReverseAssetCurrencyExchangeId',
          'payinTradeMethodsConfigs',
          'payinAmountRandomizer',
          'payoutTradeMethodsConfigs',
          'p2pProvidersConfigs',
          'payinGroupsConfigs',
          'sciDomainId',
          'useDeepLinkPaymentPage',
          'includeDeeplinkInResponse',
          'p2pProvidersCurrencyExchanges',
          'payinBlockCustomersConfigs',
          'payinBlockAmountConfigs',
          'payinCreateResponseTimeout',
          'payoutCreateOrderResponseTimeout',
          'payinCreateSyncRequisitesResponseTimeout',
          'fullWebhook',
          'webhookOverrides',
          'customerDataCollectionOrder',
          'agents',
          'payinTrustedCustomersOnly',
          'payinAmountAntiFraudRules',
          'isWithdrawalRestricted',
          'isInternalTransferRestricted',
          'sciPaymentConfigs',
          'payinDistributionSameRequisites',
          'distributionDebug',
          'userId',
          'payinTSBPFallback',
          'payinTCard2CardFallback',
          'payinTSimFallback',
          'payinTSBPNSPKTransform',
          'payinTCard2CardNSPKTransform',
          'payinNSPKInstantSyncRequisites',
          'useProviderPaymentUrl',
          'comment',
          'clarityEnabled',
        );
      }

      if (omitFields?.length) {
        shop = omit(shop, omitFields);
      }

      if (isNew) {
        createShop(shop, options);
      } else {
        updateShop({ id: shopId, data: shop }, options);
      }
    },
    [
      isNew,
      queryClient,
      navigate,
      shopDetailsPath,
      onSuccess,
      isAdmin,
      isMerchant,
      isMerchantOperator,
      createShop,
      updateShop,
      shopId,
    ],
  );

  return { handleSubmit };
};
